/**
 * The ceremony's scene: a renderer of its own, the backdrop, the cards and the
 * confetti — every one of them drawn from the frame the clock hands in.
 *
 * It is a separate renderer from the game's (`src/pixi/app.ts`) because a
 * ceremony runs over the results screen, when the table is gone: standing up a
 * small renderer for two seconds and tearing it down again costs less than
 * keeping the game's alive to composite a card over a DOM tray. It exists only
 * while a ceremony is on screen.
 *
 * Nothing here keeps time. `drawRank`/`drawLevel` are told what to draw.
 */

import { Container, Sprite, Texture, autoDetectRenderer, type Renderer } from 'pixi.js';

import { rankPalette } from '@/core/tokens';
import type { CardId } from '@/game/types';
import { bakeCardPlates } from '@/pixi/cardTexture';
import { liveQuality, type Quality } from '@/pixi/quality';
import { Backdrop } from './backdrop';
import { CAM_DIST, CardView, type CardSpec, type Plates } from './card-view';
import { bakeSpin, destroyStrip, placeSprite, type SpinStrip } from './still-bake';
import { Confetti } from './confetti';
import type { CeremonyPlan } from './plan';
import { FAN_SUITS, WILD_RANK, partnerCard, platesFor, rankCard } from './rank-cards';
import { RANK_T, escalation, finishFor, levelFrameAt, rankFrameAt, type CardPose, type LevelFrame, type RankFrame } from './timeline';
import { destroyRenderer } from '@/pixi/pixi-destroy';

/** The rank emblems (public/rank-art), by rank index. */
const EMBLEM_SLUG = ['deuce', 'seven', 'ten', 'jack', 'queen', 'king', 'ace', 'blackjack', 'wild'] as const;
export const emblemUrl = (rankIndex: number): string =>
  `${import.meta.env.BASE_URL}rank-art/rank-${Math.min(8, Math.max(0, rankIndex))}-${EMBLEM_SLUG[Math.min(8, Math.max(0, rankIndex))]}.webp`;

/** An emblem's texture, decoded; null when the file is missing (the ceremony then simply has no medallion). */
async function loadEmblem(rankIndex: number): Promise<Texture | null> {
  try {
    const img = new Image();
    img.src = emblemUrl(rankIndex);
    await img.decode();
    return Texture.from(img);
  } catch {
    return null;
  }
}

/** The medallion's glow: a soft round bloom (tinted with the rank's colour and added). */
function haloTexture(): Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 4, 64, 64, 62);
  grad.addColorStop(0, 'rgba(255,255,255,0.9)');
  grad.addColorStop(0.45, 'rgba(255,255,255,0.35)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  return Texture.from(c);
}

/** The card's contact shadow: one soft ellipse, scaled per frame. */
function shadowTexture(): Texture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 64;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 32, 2, 64, 32, 62);
  grad.addColorStop(0, 'rgba(0,0,0,0.85)');
  grad.addColorStop(0.55, 'rgba(0,0,0,0.35)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.save();
  g.scale(1, 0.5);
  g.fillRect(0, 0, 128, 128);
  g.restore();
  return Texture.from(c);
}

/**
 * How wide the medallion is, in card half-widths. The low ranks' medallions are plain discs, so they are drawn
 * larger (a ring of it shows around the card); the winged ones spread on their own; Blackjack's is drawn larger still
 * so the A+J fan does not hide it.
 */
const emblemSize = (rankIndex: number): number =>
  rankIndex === 7 ? 5.1 : (5.5 - 1.3 * Math.min(1, rankIndex / 8)) * (rankIndex === 1 ? 1.15 : 1); // Seven's ring: 15% larger (it hid behind the card)

/** How much of the smaller screen side the card's width takes. */
const CARD_SHARE = 0.46;
/** Where the card sits vertically on a full-screen ceremony. */
const CARD_Y = 0.42;
/** Half a card's height, in half-widths (the deck's aspect). */
const CARD_HALF_HEIGHT = 1.4;
/** The level flip's crest badge: its size in card half-widths, and the gap under the card (px). Shared with the DOM numeral (host.ts). */
export const BADGE_K = 0.82;
export const BADGE_GAP = 8;
export const BADGE_DROP = CARD_HALF_HEIGHT + BADGE_K * 0.5;
/**
 * How far above the camera's centre the scene reaches, in card half-widths:
 * the card's own half-height, the height it rises as it spins, and the fan's
 * spread above it.
 */
const TOP_REACH = 2.05;
/** Clear space kept above the highest thing on screen. */
const HEADROOM = 24;

interface Tier {
  /** 2 enables the shader's richer branches (thin film, sparkle, second sweep lobe). */
  readonly shader: 1 | 2;
  readonly confetti: number;
  readonly riffle: number;
  /**
   * Draw the card from a baked strip of angles instead of lighting the mesh
   * every frame — the cheapest tier's fallback. See `still-bake.ts`.
   */
  readonly baked: boolean;
}

const TIERS: Readonly<Record<Quality, Tier>> = {
  high: { shader: 2, confetti: 240, riffle: 9, baked: false },
  medium: { shader: 1, confetti: 90, riffle: 0, baked: false },
  low: { shader: 1, confetti: 36, riffle: 0, baked: true },
};

/**
 * What the confetti is cut from: foil in the rank's own colour (a deep, the colour, a light) and a gold or white
 * fleck, not the red/white/blue paper every rank threw before.
 */
function confettiColours(rankIndex: number): [number, number, number][] {
  // The top rank throws the whole spectrum (it is the one foil that is not one colour).
  if (rankIndex >= 8)
    return [
      [1.0, 0.38, 0.72],
      [1.0, 0.82, 0.3],
      [0.3, 1.0, 0.72],
      [0.4, 0.78, 1.0],
    ];
  const hex = rankPalette[Math.min(Math.max(0, rankIndex), rankPalette.length - 1)] ?? '#ffffff';
  const n = Number.parseInt(hex.slice(1), 16);
  const c: [number, number, number] = [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  const mix = (a: [number, number, number], b: [number, number, number], k: number): [number, number, number] => [
    a[0] + (b[0] - a[0]) * k,
    a[1] + (b[1] - a[1]) * k,
    a[2] + (b[2] - a[2]) * k,
  ];
  return [
    mix(c, [0, 0, 0], 0.32), // the colour, deepened
    c,
    mix(c, [1, 1, 1], 0.55), // and lit
    rankIndex >= 5 ? [1.0, 0.82, 0.4] : [0.96, 0.95, 0.92], // a gold fleck high up, white below
  ];
}

export interface StageOptions {
  readonly quality?: Quality;
  /** Full screen (the rank-up) or a small box (the tray's level flip). */
  readonly mode?: 'screen' | 'inline';
  readonly width: number;
  readonly height: number;
  readonly resolution?: number;
  /** Inline mode: the card's half-width in px, when the canvas is padded around a fixed-size card. */
  readonly px?: number;
}

export class CeremonyStage {
  readonly canvas: HTMLCanvasElement;
  readonly #renderer: Renderer;
  readonly #root = new Container();
  readonly #cards: CardView;
  readonly #backdrop: Backdrop | null;
  readonly #confetti: Confetti | null;
  readonly #tier: Tier;
  readonly #plates: Plates;
  readonly #mode: 'screen' | 'inline';
  readonly #fixedPx: number | undefined;
  readonly #plan: CeremonyPlan;
  readonly #hero: CardId;
  readonly #outCard: CardId;
  readonly #partner: CardId | null;
  readonly #fan: CardId[];
  #width: number;
  #height: number;
  #specs: CardSpec[] = [];
  /** The low tier's baked turns, by face, and the sprites that draw them. */
  readonly #strips = new Map<CardId, SpinStrip>();
  readonly #sprites: Sprite[] = [];
  readonly #baked: Container | null;
  /** Behind the card: the old rank's medallion, the new one's, and the contact shadow (screen mode only). */
  readonly #emblemOld: Sprite | null;
  readonly #emblemNew: Sprite | null;
  /** Wild II and up: a second, larger medallion turning the other way behind the first, and a glow under both. */
  readonly #emblemTwin: Sprite | null;
  /** Wild II and up: the first medallion again, added over itself (a rim light: it makes the gold brighter than Wild I's). */
  readonly #emblemRim: Sprite | null;
  readonly #halo: Sprite | null;
  /** Set once the pre-warm has drawn every pass: from then on a sprite that is faded out is not drawn at all. */
  #warmed = false;
  /** The level flip's corner badge: the rank's crest again, small, in FRONT of the card at its corner. */
  readonly #badge: Sprite | null;
  readonly #shadow: Sprite | null;

  private constructor(
    plan: CeremonyPlan,
    renderer: Renderer,
    cards: CardView,
    plates: Plates,
    backdrop: Backdrop | null,
    confetti: Confetti | null,
    tier: Tier,
    opts: StageOptions,
    art: { old: Texture | null; now: Texture | null; shadow: Texture | null; halo: Texture | null },
  ) {
    this.#plan = plan;
    this.#renderer = renderer;
    this.canvas = renderer.canvas as HTMLCanvasElement;
    this.#cards = cards;
    this.#backdrop = backdrop;
    this.#confetti = confetti;
    this.#tier = tier;
    this.#plates = plates;
    this.#mode = opts.mode ?? 'screen';
    this.#fixedPx = opts.px;
    this.#width = opts.width;
    this.#height = opts.height;
    this.#hero = rankCard(plan.in.index);
    this.#outCard = rankCard(plan.out.index);
    this.#partner = partnerCard(plan.in.index);
    this.#fan = FAN_SUITS.map((suit) => rankCard(plan.in.index, suit));
    if (backdrop) this.#root.addChild(backdrop);
    // The medallion and the shadow sit between the veil and the cards: lit by the backdrop, under the card.
    const sprite = (tex: Texture | null): Sprite | null => {
      if (!tex) return null;
      const sp = new Sprite(tex);
      sp.anchor.set(0.5);
      sp.alpha = 0;
      this.#root.addChild(sp);
      return sp;
    };
    this.#shadow = sprite(art.shadow);
    this.#halo = sprite(art.halo);
    if (this.#halo) this.#halo.blendMode = 'add';
    this.#emblemOld = sprite(art.old);
    this.#emblemTwin = plan.in.name.includes(' ') ? sprite(art.now) : null;
    this.#emblemNew = sprite(art.now);
    this.#emblemRim = plan.in.name.includes(' ') || this.#mode === 'inline' ? sprite(art.now) : null;
    if (this.#emblemRim) this.#emblemRim.blendMode = 'add';
    // Behind the cards: at the reveal the flakes were crossing the new face
    // for about ten frames, which is exactly the face the ceremony exists to
    // show. Falling behind it, they still fill the screen.
    if (confetti) this.#root.addChild(confetti);
    if (tier.baked) {
      this.#baked = new Container();
      this.#baked.sortableChildren = true;
      this.#root.addChild(this.#baked);
      cards.visible = false;
    } else {
      this.#baked = null;
      this.#root.addChild(cards);
    }
    // The level flip's badge sits over the card (added after it).
    if (this.#mode === 'inline' && art.now) {
      const b = new Sprite(art.now);
      b.anchor.set(0.5);
      b.alpha = 0;
      this.#root.addChild(b);
      this.#badge = b;
    } else this.#badge = null;
    this.#layout();
  }

  static async create(plan: CeremonyPlan, opts: StageOptions): Promise<CeremonyStage> {
    // The ceremony renders at whatever the game is rendering at: a device the
    // governor has already stepped down does not get a heavier ceremony than
    // the round it just played.
    const tier = TIERS[opts.quality ?? liveQuality()];
    const resolution = opts.resolution ?? Math.min(globalThis.devicePixelRatio || 1, 2);
    // The plates the ceremony needs, at the size it will draw them: the card
    // fills half the screen, so the table's atlas would be a 3x upscale.
    const cellWidth = Math.min(
      768,
      Math.max(256, Math.ceil((Math.min(opts.width, opts.height) * CARD_SHARE * resolution) / 32) * 32),
    );
    // No wild band on the ceremony's card, not even for the Wild rank. That band is the TABLE's marking for the two
    // black jacks in play; on the hero card it was a dark plum bar across the court figure with a tiny "WILD"
    // stamped in it (it read as a render seam and a watermark). The Wild rank's identity is its spectrum foil, its
    // medallion and its name.
    const wild: CardId[] = [];
    // The inline flip shows one card and only one, so it bakes one plate: the
    // fan and the pair belong to the full-screen rank-up. Baking all six for a
    // 1.2 s flip in a 132 px box was most of the time it took to appear.
    const ids =
      (opts.mode ?? 'screen') === 'inline'
        ? [rankCard(plan.in.index)]
        : platesFor(plan.out.index, plan.in.index);
    const plates = await bakeCardPlates(ids, cellWidth, wild);
    const renderer = await autoDetectRenderer({
      preference: 'webgl',
      width: opts.width,
      height: opts.height,
      resolution,
      backgroundAlpha: 0,
      antialias: true,
      autoDensity: true,
    });
    const cards = new CardView(plates, tier.shader);
    const backdrop =
      (opts.mode ?? 'screen') === 'screen'
        ? new Backdrop(colourOf(plan.out.index), colourOf(plan.in.index))
        : null;
    const confetti =
      (opts.mode ?? 'screen') === 'screen' && tier.confetti > 0
        ? new Confetti(
            // The higher the rank, the more is thrown, kept modest (the Wild tier's GPU time is the highest of the ceremony).
            Math.round(tier.confetti * (0.6 + 0.55 * escalation(plan.in) + (plan.in.name.includes(' ') ? 0.2 : 0))),
            (RANK_T.confetti[1] - RANK_T.confetti[0]) / 1000,
            confettiColours(plan.in.index),
          )
        : null;
    const screen = (opts.mode ?? 'screen') === 'screen';
    // The full-screen rank-up loads the old and the new medallion; the level flip only the rank it is in.
    const [old, now] = screen
      ? await Promise.all([loadEmblem(plan.out.index), loadEmblem(plan.in.index)])
      : [null, await loadEmblem(plan.in.index)];
    const stage = new CeremonyStage(plan, renderer, cards, plates, backdrop, confetti, tier, opts, {
      old,
      now: now,
      shadow: screen ? shadowTexture() : null,
      halo: haloTexture(),
    });
    await stage.#warm();
    stage.#warmed = true;
    return stage;
  }

  /**
   * Draw every pass once before the clock starts: the veil and its old medallion, then the swap's frame (the new
   * face, the fan, the riffle, the confetti, the new medallion). A shader links and a texture uploads on the frame
   * that first DRAWS it, and the swap was where the reveal dropped frames. Nothing here is ever on screen: the
   * canvas is not in the page until the overlay is appended, and the first real frame draws over it.
   */
  async #warm(): Promise<void> {
    // One pass per task: a program link is 55-110 ms on its own, and the three together were one ~200 ms freeze.
    const tick = (): Promise<void> => new Promise((r) => setTimeout(r, 0));
    if (this.#mode === 'screen') {
      for (const ms of [300, RANK_T.swap + 200, 0]) {
        this.drawRank(rankFrameAt(this.#plan, ms));
        await tick();
      }
    } else {
      for (const ms of [400, 0]) {
        this.drawLevel(levelFrameAt(this.#plan, ms));
        await tick();
      }
    }
  }

  resize(width: number, height: number): void {
    this.#width = width;
    this.#height = height;
    this.#renderer.resize(width, height);
    this.#layout();
  }

  #layout(): void {
    const px = this.#pxPerUnit();
    this.#cards.camera(this.#width / 2, this.#height * (this.#mode === 'screen' ? CARD_Y : 0.5), px);
    this.#backdrop?.layout(this.#width, this.#height, this.#width / 2, this.#height * CARD_Y);
  }

  #pxPerUnit(): number {
    if (this.#mode === 'inline') return this.#fixedPx ?? Math.min(this.#width * 0.5, this.#height * 0.42);
    const share = Math.min(this.#width, this.#height) * CARD_SHARE;
    /*
     * On a wide screen the card is sized by the HEIGHT, not the width.
     *
     * A 16:9 desktop has width to spare and none to give vertically, and the
     * card is not the tallest thing in the scene: it rises a card-height as it
     * spins, and the four-suit fan reaches higher still. Sized off the short
     * side alone, the 10♠ was cropped by the top edge at the rise and the J♠
     * fan at the landing. `TOP_REACH` is how far above the camera's centre the
     * scene actually goes, in card half-widths, so this is the size at which
     * all of it fits with `HEADROOM` to spare.
     */
    const centreY = this.#height * CARD_Y;
    const byHeight = (centreY - HEADROOM) / TOP_REACH;
    return Math.min(share / 2, byHeight);
  }

  /** Where the card's resting bottom edge is, in pixels — the text sits under it. */
  #cardBottom(): number {
    return this.#height * CARD_Y + this.#pxPerUnit() * CARD_HALF_HEIGHT;
  }

  /** Where the card is, for the overlay's medallion and shadow: its centre's y and its half-width, in px. */
  get placement(): { cy: number; px: number } {
    return { cy: this.#height * CARD_Y, px: this.#pxPerUnit() };
  }

  /** The text block's top, published to the overlay so the two read as one group. */
  get textTop(): number {
    return Math.round(this.#cardBottom() + 18);
  }

  /** The full-screen rank-up at this frame. */
  drawRank(f: RankFrame): void {
    const specs = this.#specs;
    specs.length = 0;
    const t = f.t / 1000;
    const heat = f.card.heat;
    const scale = f.card.scale;

    // The fan: the rank's other three suits arcing out behind, so all four
    // suits of the new rank are on screen at the reveal.
    if (f.fan > 0.001) {
      for (let i = 0; i < this.#fan.length; i++) {
        // The first ranks' fan is tucked in (Seven's medallion, ring and wing tips, were hidden behind it at 360).
        const fanK = this.#plan.in.index <= 1 ? 0.68 : this.#plan.in.index === 2 ? 0.86 : 1;
        const spread = (i - 1) * 26 * f.fan * (0.6 + 0.4 * fanK);
        const rad = (spread * Math.PI) / 180;
        specs.push({
          face: this.#fan[i]!,
          yaw: f.card.yaw + spread * 0.35,
          pitch: f.card.pitch,
          roll: spread,
          x: Math.sin(rad) * 1.6 * f.fan * fanK,
          y: (Math.cos(rad) - 1) * 0.5 + f.card.lift * 0.45 + 0.28 * f.fan,
          z: -0.45 - i * 0.06,
          scale: scale * 0.84 * (0.88 + 0.12 * fanK),
          finish: f.card.finish,
          rankIndex: f.card.rank.index,
          heat: heat * 0.5,
          sweep: f.card.sweep,
          alpha: Math.min(1, f.fan * 1.4),
        });
      }
    }

    // The riffle: a run of backs flicking past BEHIND the card, on a diagonal.
    // Behind, and never over it: the reveal is the one thing on this screen
    // that may not be covered, and a shuffle across it hides exactly the
    // frame the ceremony exists for.
    if (this.#tier.riffle > 0 && f.riffle > 0 && f.riffle < 1) {
      for (let k = 0; k < this.#tier.riffle; k++) {
        const u = f.riffle * 1.5 - k * 0.075;
        if (u <= 0 || u >= 1) continue;
        specs.push({
          face: this.#hero,
          yaw: 180 + u * 52,
          pitch: -14 + u * 26,
          roll: -34 + u * 62,
          x: 3.4 - u * 6.8,
          y: 1.5 - u * 2.2,
          z: -1.1 - (k % 3) * 0.12,
          scale: scale * 0.62,
          finish: f.card.finish,
          rankIndex: f.card.rank.index,
          alpha: Math.sin(Math.PI * u) * 0.7,
        });
      }
    }

    // Blackjack: the jack that makes it a hand, splitting away from the ace.
    if (this.#partner && f.pair > 0.001) {
      specs.push({
        face: this.#partner,
        yaw: f.card.yaw - 6 * f.pair,
        pitch: f.card.pitch,
        roll: -13 * f.pair,
        x: -0.62 * f.pair,
        y: f.card.lift * 0.45 - 0.05 * f.pair,
        z: -0.12,
        scale,
        finish: f.card.finish,
        rankIndex: f.card.rank.index,
        heat,
        sweep: f.card.sweep,
        alpha: 1,
      });
    }

    // The card itself.
    specs.push({
      face: f.swapped ? this.#hero : this.#outCard,
      yaw: f.card.yaw,
      pitch: f.card.pitch,
      roll: this.#partner ? 9 * f.pair : 0,
      x: this.#partner ? 0.5 * f.pair : 0,
      y: f.card.lift * 0.45,
      z: 0,
      scale,
      finish: f.card.finish,
      rankIndex: f.card.rank.index,
      heat,
      sweep: f.card.sweep,
      alpha: 1,
    });

    this.#backdrop?.draw({
      veil: f.veil,
      wipe: f.wipe,
      ring: f.ring,
      flash: f.flash,
      rays: f.rays,
      gather: f.gather,
      time: t,
    });
    this.#placeBehind(f);
    this.#paint(specs, t);
    this.#confetti?.draw({
      time: (f.t - RANK_T.confetti[0]) / 1000,
      x: this.#width / 2,
      y: this.#height * CARD_Y,
      alpha: f.swapped ? 1 : 0,
      keepAbove: this.textTop - 10,
    });
    this.#renderer.render(this.#root);
  }

  /** The medallion(s), their glow and the contact shadow, from the frame: transforms and alpha only. */
  #placeBehind(f: RankFrame): void {
    const { cy, px } = this.placement;
    const cx = this.#width / 2;
    // Kept inside the screen: the widest thing drawn (the twin, at its steady size) fits the width.
    const twinK = this.#emblemTwin ? 1.2 : 1;
    const size = Math.min(emblemSize(this.#plan.in.index), (this.#width * 0.995) / px / twinK);
    const fit = (sp: Sprite | null, k: number): number => (sp ? (px * size * k) / sp.texture.width : 0);
    // The first ranks' medallion is lifted a little, so its wings and ring show above and beside the card.
    const lift = this.#plan.in.index <= 1 ? 0.2 : this.#plan.in.index === 2 ? 0.08 : 0;
    const my = cy - px * (0.1 + lift);
    // Never exactly 0: an invisible sprite is skipped, and its texture would upload on the frame it first shows.
    if (this.#emblemOld) {
      this.#emblemOld.alpha = Math.max(0.003, f.emblem.oldAlpha);
      this.#emblemOld.scale.set(fit(this.#emblemOld, f.emblem.scale));
      this.#emblemOld.position.set(cx, my);
    }
    if (this.#emblemNew) {
      this.#emblemNew.alpha = Math.max(0.003, f.emblem.newAlpha);
      this.#emblemNew.scale.set(fit(this.#emblemNew, f.emblem.scale));
      this.#emblemNew.rotation = (f.emblem.rot * Math.PI) / 180;
      this.#emblemNew.position.set(cx, my);
    }
    if (this.#emblemTwin) {
      // Wild II and beyond: the same crest again, larger and turning against the first, left in its own bright
      // gold-white (it was tinted amber and read as dull bronze): the top of the ladder is two medallions deep.
      this.#emblemTwin.alpha = Math.max(0.003, f.emblem.newAlpha * 0.85);
      this.#emblemTwin.scale.set(fit(this.#emblemTwin, f.emblem.scale * 1.2));
      this.#emblemTwin.rotation = (-f.emblem.rot * 0.7 * Math.PI) / 180 + 0.22;
      this.#emblemTwin.position.set(cx, my);
    }
    if (this.#emblemRim) {
      // The rim light: the first medallion added over itself, so its gold and its spikes blaze.
      this.#emblemRim.alpha = Math.max(0.003, f.emblem.newAlpha * 0.55);
      this.#emblemRim.scale.set(fit(this.#emblemRim, f.emblem.scale));
      this.#emblemRim.rotation = (f.emblem.rot * Math.PI) / 180;
      this.#emblemRim.position.set(cx, my);
    }
    if (this.#halo) {
      const k = f.emblem.newAlpha;
      this.#halo.alpha = Math.max(0.003, k * (0.45 + 0.25 * f.esc + 0.3 * f.ascend));
      this.#halo.tint = f.ascend > 0 ? 0xfff0c0 : parseInt((rankPalette[Math.min(Math.max(0, this.#plan.in.index), rankPalette.length - 1)] ?? '#ffffff').slice(1), 16);
      this.#halo.width = px * size * 0.95;
      this.#halo.height = px * size * 0.95;
      this.#halo.position.set(cx, my);
    }
    if (this.#shadow) {
      this.#shadow.alpha = f.shadow.alpha;
      this.#shadow.width = px * 2.8 * f.shadow.scale;
      this.#shadow.height = px * 0.62 * f.shadow.scale;
      this.#shadow.position.set(cx, cy + px * CARD_HALF_HEIGHT + px * 0.12);
    }
    this.#cull();
  }

  /**
   * The medallions, the glow and the shadow sit at alpha 0.003 while faded out (so the pre-warm uploads them). Once
   * warmed, a sprite that is not showing is skipped instead of blending a screen-sized quad for nothing: the old
   * medallion after the swap, the new one before it, the glow before it. (This was Seven's extra GPU time at round 2.)
   */
  #cull(): void {
    if (!this.#warmed) return;
    for (const sp of [this.#emblemOld, this.#emblemNew, this.#emblemTwin, this.#emblemRim, this.#halo, this.#shadow, this.#badge]) {
      if (sp) sp.visible = sp.alpha > 0.004;
    }
  }

  /** The tray's level flip. */
  drawLevel(f: LevelFrame): void {
    this.#placeLevel(f);
    this.#drawOne(f.card, f.t / 1000);
  }

  /**
   * The level flip's medallion and glow, behind the card (the same system as the rank-up: a crest and its light),
   * a size down so it never reaches far over the tray's row. It brightens and pops as the new pip lights.
   */
  #placeLevel(f: LevelFrame): void {
    const px = this.placement.px;
    const cx = this.#width / 2;
    const cy = this.#height / 2;
    const k = Math.min(1, f.t / 260);
    // At the settle the crest is LIT: its wings and crown take a rim of light (the old one sat dim and brown behind the
    // card) and the glow behind stays up instead of dying with the turn.
    const lit = Math.min(1, Math.max(0, (f.t - 500) / 450));
    const fit = (sp: Sprite, size: number): number => size / sp.texture.width;
    if (this.#emblemNew) {
      const size = Math.min(px * 3.2, this.#width) * (1 + 0.08 * f.pipPop);
      this.#emblemNew.alpha = Math.max(0.003, k);
      this.#emblemNew.scale.set(fit(this.#emblemNew, size));
      this.#emblemNew.rotation = (Math.sin(f.t / 500) * 3 * Math.PI) / 180;
      this.#emblemNew.position.set(cx, cy);
      if (this.#emblemRim) {
        this.#emblemRim.alpha = Math.max(0.003, 0.55 * lit * k);
        this.#emblemRim.scale.set(fit(this.#emblemRim, size));
        this.#emblemRim.rotation = this.#emblemNew.rotation;
        this.#emblemRim.position.set(cx, cy);
      }
    }
    if (this.#badge) {
      // The crest again, seated bottom-centre just BELOW the card (it sat on the card's top-right corner, over the face and
      // crowding the LEVEL pill above): clear of the face and of the pill, and it pops as the new pip lights.
      const size = px * BADGE_K * (1 + 0.35 * f.pipPop);
      this.#badge.alpha = Math.max(0.003, k);
      this.#badge.scale.set(size / this.#badge.texture.width);
      this.#badge.rotation = 0;
      this.#badge.position.set(cx, cy + px * CARD_HALF_HEIGHT + px * BADGE_K * 0.5 + BADGE_GAP);
    }
    if (this.#halo) {
      const tint = rankPalette[Math.min(Math.max(0, this.#plan.in.index), rankPalette.length - 1)] ?? '#ffffff';
      this.#halo.tint = parseInt(tint.slice(1), 16);
      this.#halo.alpha = Math.max(0.003, Math.max(f.glow, 0.65 * lit) * 0.85);
      this.#halo.width = Math.min(px * 4, this.#width * 1.1);
      this.#halo.height = Math.min(px * 4, this.#width * 1.1);
      this.#halo.position.set(cx, cy);
    }
    this.#cull();
  }

  /** The card at rest — what the tray shows before and after a ceremony. */
  drawRest(pose: CardPose): void {
    this.#drawOne(pose, 0);
  }

  #drawOne(pose: CardPose, timeSeconds: number): void {
    const specs = this.#specs;
    specs.length = 0;
    specs.push({
      face: rankCard(pose.rank.index),
      yaw: pose.yaw,
      pitch: pose.pitch,
      y: pose.lift * 0.2,
      scale: pose.scale,
      finish: finishFor(pose.rank),
      rankIndex: pose.rank.index,
      heat: pose.heat,
      sweep: pose.sweep,
      alpha: 1,
    });
    this.#paint(specs, timeSeconds);
    this.#renderer.render(this.#root);
  }

  /**
   * Draw the cards: the lit mesh, or — on the low tier — sprites out of the
   * baked strips, which are baked on first use from that same mesh.
   */
  #paint(specs: readonly CardSpec[], timeSeconds: number): void {
    const baked = this.#baked;
    if (!baked) {
      this.#cards.draw(specs, timeSeconds);
      return;
    }
    const camera = {
      cx: this.#width / 2,
      cy: this.#height * (this.#mode === 'screen' ? CARD_Y : 0.5),
      pxPerUnit: this.#pxPerUnit(),
      camDist: CAM_DIST,
    };
    while (this.#sprites.length < specs.length) {
      const sprite = new Sprite();
      this.#sprites.push(sprite);
      baked.addChild(sprite);
    }
    for (let i = 0; i < this.#sprites.length; i++) {
      const sprite = this.#sprites[i]!;
      const spec = specs[i];
      if (!spec) {
        sprite.visible = false;
        continue;
      }
      let strip = this.#strips.get(spec.face);
      if (!strip) {
        strip = bakeSpin(
          this.#renderer,
          this.#plates,
          spec.face,
          spec.finish,
          spec.rankIndex,
          Math.min(camera.pxPerUnit, 150),
        );
        this.#strips.set(spec.face, strip);
      }
      placeSprite(sprite, strip, spec, camera);
      sprite.zIndex = spec.z ?? 0;
    }
  }

  get plan(): CeremonyPlan {
    return this.#plan;
  }

  destroy(): void {
    for (const strip of this.#strips.values()) destroyStrip(strip);
    this.#strips.clear();
    this.#baked?.destroy();
    this.#cards.destroy();
    this.#backdrop?.destroy();
    this.#confetti?.destroy();
    for (const sp of [this.#emblemOld, this.#emblemNew, this.#shadow, this.#halo]) sp?.texture.destroy(true);
    // (the badge shares the new emblem's texture)
    this.#root.destroy();
    destroyRenderer(this.#renderer);
  }
}

function colourOf(index: number): string {
  // The top rank's ground is a deep violet (its own colour is a near-white gold, which made the peak the muddiest,
  // greyest screen); the accent elsewhere stays the rank's gold.
  if (index >= 8) return '#8f5cff';
  return rankPalette[Math.min(Math.max(0, index), rankPalette.length - 1)] ?? '#ffffff';
}
